import { useState } from 'react';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import { ListQuestionOptions } from 'model/reports/ListResults';
import QuestionType, {
  getQuestionInstructions,
} from '@ax2/common/model/QuestionType';
import SurveySchema from '@ax2/common/model/schemas/SurveySchema';
import { getTemplateText } from '@ax2/common/helpers/template';
import { ExportData } from 'model/reports/export/ExportData';
import { exportAsPDF } from 'reports/exports/exportPDF';
import _ from 'lodash';

const usePdfDownload = (
  surveySchema: SurveySchema | null
): { loading: boolean; pdfDownload: () => void } => {
  const [loading, setLoading] = useState(false);
  const getAnswerOptionsData = (
    question: QuestionSchema
  ): ListQuestionOptions => {
    const helperText = getQuestionInstructions(question, true);
    switch (question.questionType) {
      case QuestionType.Empty:
      case QuestionType.OpenEndedText:
      case QuestionType.OpenEndedNumeric:
        return {
          qType: question.questionType,
          exportType: 'TextBoxField' as const,
          helperText,
        };
      case QuestionType.CheckboxGrid:
      case QuestionType.TextboxGridNumeric:
      case QuestionType.RadioButtonGrid:
      case QuestionType.RadioButtonGridRow:
        return {
          columns: question.columns,
          rows: question.rows,
          qType: question.questionType,
          exportType: 'ColumnRows' as const,
          helperText,
        };
      case QuestionType.RatingList:
        return {
          columns: question.columns,
          rows: question.rows,
          qType: question.questionType,
          exportType: 'RatingBar' as const,
          helperText,
        };
      case QuestionType.DropdownMenu:
      case QuestionType.CheckboxList:
      case QuestionType.RadioButtonList:
      case QuestionType.TextboxListText:
      case QuestionType.TextboxListNumeric:
      case QuestionType.ContinuousSumList:
      case QuestionType.RankingList:
        return {
          options: question.answerOptions,
          qType: question.questionType,
          exportType: 'ListOptions' as const,
          helperText,
        };
      default:
        throw Error(`Unknown QuestionType was used in report builder.`);
    }
  };

  const pdfDownload = async () => {
    if (surveySchema) {
      try {
        setLoading(true);
        const ansData = _.map(surveySchema.questions, question => {
          const tempText = getTemplateText(question.content);
          const ansOps = getAnswerOptionsData(question);
          const data: ExportData = {
            text: tempText,
            type: ansOps ? ansOps.exportType : 'ListOptions',
            data: ansOps || [],
          };
          return data;
        });
        await exportAsPDF(ansData, surveySchema.title || '');
      } catch (error) {
        console.log('Error while downloading PDF of the Survey: ', error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    }
  };

  return { loading, pdfDownload };
};

export { usePdfDownload };
